import { alpha } from '@mui/material/styles';

// ----------------------------------------------------------------------

function createGradient(color1, color2) {
  return `linear-gradient(to bottom, ${color1}, ${color2})`;
}

// SETUP COLORS — DataWorld brand
// Primary = electric indigo (matches the DataWorld mobile app #2728FF)
// Secondary = violet accent (globe orbit / data motif)
const PRIMARY = {
  lighter: '#EAEAFF',
  light: '#6E70FF',
  main: '#2728FF',
  dark: '#1A1BC2',
  darker: '#101086',
};
const SECONDARY = {
  lighter: '#F3ECFF',
  light: '#B18CFF',
  main: '#8B5CF6',
  dark: '#6D3EDA',
  darker: '#4A249E',
};
const INFO = {
  lighter: '#D0F2FF',
  light: '#74CAFF',
  main: '#1890FF',
  dark: '#0C53B7',
  darker: '#04297A',
};
const SUCCESS = {
  lighter: '#E9FCD4',
  light: '#AAF27F',
  main: '#54D62C',
  dark: '#229A16',
  darker: '#08660D',
};
const WARNING = {
  lighter: '#FFF7CD',
  light: '#FFE16A',
  main: '#FFC107',
  dark: '#B78103',
  darker: '#7A4F01',
};
const ERROR = {
  lighter: '#FFE7D9',
  light: '#FFA48D',
  main: '#FF4842',
  dark: '#B72136',
  darker: '#7A0C2E',
};

const GREY = {
  0: '#FFFFFF',
  100: '#F6F9FF',
  200: '#EEF2FA',
  300: '#E3E9F5',
  400: '#C4CDDF',
  500: '#8A95B0',
  600: '#5A6B8A',
  700: '#384057',
  800: '#1A2238',
  900: '#0B1437',
  500_8: alpha('#8A95B0', 0.08),
  500_12: alpha('#8A95B0', 0.12),
  500_16: alpha('#8A95B0', 0.16),
  500_24: alpha('#8A95B0', 0.24),
  500_32: alpha('#8A95B0', 0.32),
  500_48: alpha('#8A95B0', 0.48),
  500_56: alpha('#8A95B0', 0.56),
  500_80: alpha('#8A95B0', 0.8),
};

const GRADIENTS = {
  primary: `linear-gradient(135deg, ${PRIMARY.dark} 0%, ${PRIMARY.main} 50%, ${SECONDARY.main} 100%)`,
  info: createGradient(INFO.light, INFO.main),
  success: createGradient(SUCCESS.light, SUCCESS.main),
  warning: createGradient(WARNING.light, WARNING.main),
  error: createGradient(ERROR.light, ERROR.main),
};

const CHART_COLORS = {
  violet: ['#826AF9', '#9E86FF', '#D0AEFF', '#F7D2FF'],
  blue: ['#2728FF', '#6E70FF', '#B18CFF', '#EAEAFF'],
  green: ['#2CD9C5', '#60F1C8', '#A4F7CC', '#C0F2DC'],
  yellow: ['#FFE700', '#FFEF5A', '#FFF7AE', '#FFF3D6'],
  red: ['#FF6C40', '#FF8F6D', '#FFBD98', '#FFF2D4'],
};

const COMMON = {
  common: { black: '#000', white: '#fff' },
  primary: { ...PRIMARY, contrastText: '#fff' },
  secondary: { ...SECONDARY, contrastText: '#fff' },
  info: { ...INFO, contrastText: '#fff' },
  success: { ...SUCCESS, contrastText: GREY[800] },
  warning: { ...WARNING, contrastText: GREY[800] },
  error: { ...ERROR, contrastText: '#fff' },
  grey: GREY,
  gradients: GRADIENTS,
  chart: CHART_COLORS,
  divider: GREY[500_24],
  action: {
    hover: GREY[500_8],
    selected: GREY[500_16],
    disabled: GREY[500_80],
    disabledBackground: GREY[500_24],
    focus: GREY[500_24],
    hoverOpacity: 0.08,
    disabledOpacity: 0.48,
  },
};

const palette = {
  light: {
    ...COMMON,
    mode: 'light',
    text: { primary: GREY[900], secondary: GREY[600], disabled: GREY[500] },
    background: { paper: '#fff', default: GREY[100], neutral: GREY[200] },
    action: { active: GREY[600], ...COMMON.action },
  },
  dark: {
    ...COMMON,
    mode: 'dark',
    text: { primary: '#fff', secondary: GREY[400], disabled: GREY[600] },
    background: { paper: GREY[800], default: GREY[900], neutral: GREY[500_16] },
    action: { active: GREY[400], ...COMMON.action },
  },
};

export default palette;
